import React, { useEffect, useState } from "react";
import { Star, Check } from "lucide-react";
import { toast } from "sonner";
import { endpoints } from "@/lib/api";

/**
 * SaveToPracticeButton — a quiet star button that lets a member add an
 * Essentials-style ritual (Movement, Sleep, Lymphatic Massage, etc.)
 * to their personal Your Practice tab.
 *
 * Persistence:
 *  • Calls `endpoints.createRitual` to save the ritual to the user's
 *    Rituals list.
 *  • Records "already saved" in localStorage under `saved-ritual:<slug>`
 *    so the button doesn't allow silent duplicates across page reloads.
 */
export default function SaveToPracticeButton({
  slug,
  title,
  description = "",
  steps = [],
  timeOfDay = "anytime",
  durationMin = 10,
  testid = "save-to-practice",
}) {
  const storageKey = `saved-ritual:${slug}`;
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    try {
      if (localStorage.getItem(storageKey)) setSaved(true);
    } catch { /* noop */ }
  }, [storageKey]);

  const onClick = async () => {
    if (saved || saving) return;
    setSaving(true);
    try {
      await endpoints.createRitual({
        title,
        description,
        steps,
        time_of_day: timeOfDay,
        duration_min: durationMin,
        category: "essentials",
      });
      try { localStorage.setItem(storageKey, String(Date.now())); } catch { /* noop */ }
      setSaved(true);
      toast.success("Held in Your Practice.");
    } catch {
      toast.error("Couldn't save just yet — try again in a moment.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={saved || saving}
      data-testid={testid}
      aria-pressed={saved}
      className={`inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-[11px] uppercase tracking-[0.24em] border hairline tab-link ${
        saved
          ? "bg-[hsl(var(--copper))]/12 text-[hsl(var(--copper))] border-[hsl(var(--copper))]/40"
          : "text-[hsl(var(--fg))]/75 hover:text-[hsl(var(--copper))] hover:border-[hsl(var(--copper))]/40"
      }`}
    >
      {saved ? <Check size={12} /> : <Star size={12} />}
      <span>
        {saved ? "In Your Practice" : saving ? "Saving…" : "Save to Your Practice"}
      </span>
    </button>
  );
}
